import { mapState } from 'pinia';
import languageDataMixin from './languageDataMixin';
import { findCategoryById } from '~~/common/utils/helper';
import { webpFallback } from '~~/common/utils';
import { PCAT_ADD_PRODUCT, PUSH_COMPARE_PRODUCT } from '~~/common/plugins/aw-analytics.js';
import { UnCachedProduct } from '~~/common/stores/products';
import { useModalStore } from '~~/common/stores/modal';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { useCategoryStore } from '~~/common/stores/category';
import { useCompareStore } from '~~/common/stores/compare';
import { useProductStore } from '~~/common/stores/product';
import { useDeliveryStore } from '~~/shop/app/stores/delivery';
import { useVideoStore } from '~~/common/stores/video';
import { determinePriceType } from '~~/common/utils/productPriceType';
import { useProductPricesByVariant, useRollPriceByVariant } from '~~/common/composables/useProductPrices';
import {
  getAllDisplayedFlags,
  getBlocks,
  getDocuments,
  getEanCode,
  getIsAided,
  getIsInVirtualStock,
  getProduct,
  getProductCategoryId,
  getProductId,
  getProductName,
  getShowedStatusMessageFull,
  getSku,
  getStockInfos,
  getThumbImageByVariant,
  getVariant,
  getVariantId,
  isReviewVisible,
  USED_VARIANT_PROPERTY,
} from '~~/common/utils/productData';

export default {
  mixins: [
    languageDataMixin,
  ],
  data () {
    return {
      showNotificationRequestModal: false,
    };
  },
  computed: {
    ...mapState(useCompareStore, {
      compareItems: state => state.compareItems,
      maxCompareCount: state => state.maxCompareCount,
    }),
    ...mapState(useUserInterfaceStore, {
      screenRange: state => state.mediaQueries,
      screenSize: state => state.mediaQueries.query,
    }),
    ...mapState(useProductStore, {
      onSale (state) {
        return state.isPriceDiscounted(this.getVariant?.price);
      },
    }),
    ...mapState(useDeliveryStore, {
      deliveryMethod: state => state.setup,
    }),
    product () {
      return getProduct(this);
    },
    getUnCachedProduct () {
      return (this.product?.feMeta?.isCached
        ? undefined
        : new UnCachedProduct({
          productId: this.productId,
          shoppingListsContain: this.getVariant?.shoppingListsContain,
          status: this.getVariant?.cartInfo?.availability,
          isLoyaltyPriceValid: this.getVariant?.isLoyaltyPriceValid,
        })
      );
    },
    getVariant () {
      return getVariant(this.product, USED_VARIANT_PROPERTY);
    },
    getCategories () {
      return this.product.categories;
    },
    productId () {
      return getProductId(this.product);
    },
    productCategoryId () {
      return getProductCategoryId(this.product);
    },
    variantId () {
      return getVariantId(this.getVariant);
    },
    productName () {
      return getProductName(this.getVariant);
    },
    variantUnit () {
      return ['db', 'piece', 'szt.'].includes(this.getVariant.unit) ? 'db' : this.getVariant.unit; // szt. -> PL unit
    },
    /* @deprecated isAided currently unused */
    isAided () {
      return getIsAided(this.getVariant);
    },
    eanCode () {
      return getEanCode(this.product);
    },
    documents () {
      return getDocuments(this.product);
    },
    sku () {
      return getSku(this.getVariant);
    },
    images () {
      return this.getVariant?.media?.mainImage ? [].concat([this.getVariant?.media?.mainImage], this.getVariant.media.images.filter(item => item !== this.getVariant?.media?.mainImage)) : (this.getVariant?.media?.images ? this.getVariant.media.images : []);
    },
    videos () {
      return this.getVariant?.media?.videos ? this.getVariant.media.videos : [];
    },
    badgesData () {
      return this.getVariant?.flags || [];
    },
    displayedFlags () {
      return getAllDisplayedFlags(this.getVariant);
    },
    isInVirtualStock () {
      return getIsInVirtualStock(this.getVariant);
    },
    stockInfos () {
      return getStockInfos(this.getVariant);
    },
    reviewable () {
      return this.product?.reviewable;
    },
    visibleReviewBlock () {
      return isReviewVisible(this.reviewable);
    },
    reviewSum () {
      return this.product?.reviewSum;
    },
    reviewAverageValue () {
      return this.reviewSum?.average;
    },
    reviewSumCountValue () {
      return this.reviewSum?.sumCount;
    },
    discountPercentage () {
      return this.getVariant?.price?.discountDisplayPercentage;
    },
    weightPerPieceRatio () {
      return this.getVariant?.loose?.weightPerPieceRatio || 0;
    },
    maxQuantityChoices () {
      return this.getVariant?.cartInfo?.maxQuantityChoices || 1;
    },
    quantityStepSize () {
      return this.getVariant?.cartInfo?.quantityStepSize || 0;
    },
    showedStatusMessageFull () {
      return getShowedStatusMessageFull({ availability: this.getUnCachedProduct?.status });
    },
    /* See: 8cbe590a9dd6f5340ca8685fbfcc22164bf966dd
    TEMPORARY REMOVED THE NOTIFICATION BUTTON
    showStockNotificationRequestLink () {
      return ['unavailable', 'notAvailableOnline'].includes(this.getVariant?.cartInfo?.availability);
    }, */
    price () {
      return this.getVariant.price;
    },
    priceUnitGross () {
      const prices = this.getUnitPriceByType('grossDiscounted');
      return `${prices.unitPrice}${(prices.unitPrice && prices.pricePerKg) ? '<br>' : ''}${prices.pricePerKg}`;
    },
    priceUnitOriginalGross () {
      const prices = this.getUnitPriceByType('gross');
      return `${prices.unitPrice}${(prices.unitPrice && prices.pricePerKg) ? '<br>' : ''}${prices.pricePerKg}`;
    },
    rollPrice () {
      const { getRollPriceByVariant } = useRollPriceByVariant();
      return getRollPriceByVariant({
        variant: this.getVariant,
      });
    },
    rollType () {
      return this.getVariant?.roll?.type;
    },
    /* @deprecated priceOriginalGross Not in use */
    priceOriginalGross () {
      return this.priceOriginalGrossByVariant(this.getVariant);
    },
    productPriceType () {
      return determinePriceType({
        variant: this.getVariant,
        isLoyaltyPriceValid: this.getUnCachedProduct?.isLoyaltyPriceValid,
      });
    },
    productPrices () {
      return useProductPricesByVariant()({
        variant: this.getVariant,
        productPriceType: this.productPriceType,
      });
    },
    productPricesForOneUnit () {
      return useProductPricesByVariant()({
        variant: this.getVariant,
        productPriceType: this.productPriceType,
        isOneUnit: true,
      });
    },
    isUnitTrivial () {
      return !!(
        this.productPrices?.secondary?.units?.length === 1 &&
          this.productPrices.secondary.unit?.startsWith?.(this.productPrices.secondary.main)
      );
    },
    isUnitTrivialForOneUnit () {
      return !!(
        this.productPricesForOneUnit?.secondary?.units?.length === 1 &&
          this.productPricesForOneUnit.secondary.unit?.startsWith?.(this.productPricesForOneUnit.secondary.main)
      );
    },
    notAppliedPromoPriceStr () {
      const {
        main,
        units,
      } = this.productPrices.secondary;
      return main + (units.length ? `${units.length > 1 ? '<br>' : ' ('}${units.join('<br>')}${units.length > 1 ? '' : ')'}` : '');
    },
    itemVolumeInfo () {
      return this.getVariant?.itemVolumeInfo;
    },
    /* @deprecated rollInfo currently unused */
    rollInfo () {
      return this.rollPrice
        ? this.$awt(`aw.common.product.${this.rollType === 'redemption' ? 'redemption_price' : 'roll_price'}`, { price: this.rollPrice })
        : null;
    },
    deliveryPrice () {
      return this.getVariant?.deliveryPrice;
    },
    getBlocks () {
      return getBlocks(this.product, this.getVariant);
    },
    showDescriptionBlock () {
      return this.getBlocks.showDescriptionBlock;
    },
    showParameterListBlock () {
      return this.getBlocks.showParameterListBlock;
    },
    showNutritionBlock () {
      return this.getBlocks.showNutritionBlock;
    },
    showAllergensBlock () {
      return this.getBlocks.showAllergensBlock;
    },
    showAllergensDetailedBlock () {
      return this.getBlocks.showAllergensDetailedBlock;
    },
    showIngredientsBlock () {
      return this.getBlocks.showIngredientsBlock;
    },
    showIngredientsDetailedBlock () {
      return this.getBlocks.showIngredientsDetailedBlock;
    },
    showDocumentsBlock () {
      return this.getBlocks.showDocumentsBlock;
    },
    showDataEraserBlock () {
      return this.getBlocks.showDataEraserBlock;
    },
    showCetelemBlock () {
      return this.getBlocks.showCetelemBlock;
    },
    thumbImage () {
      return getThumbImageByVariant(this.getVariant);
    },
    image () {
      const media = this.getVariant?.media || {};

      return webpFallback([{
        url: media?.listImage || media?.mainImage || media?.images?.[0] || '',
      }]);
    },
    creditStatus () {
      return this.product?.creditStatus;
    },
    isHighlighted () {
      return this.product?.isHighlighted;
    },
  },
  methods: {
    doCompare () {
      const compareStore = useCompareStore();
      const categoryStore = useCategoryStore();
      const modalStore = useModalStore();
      // find current compare product category data
      const currentItemCategoryData = findCategoryById(
        parseInt(this.product?.categoryId),
        categoryStore.categories?.children,
      );
      // find the first compared product (already on compare list) category data
      const compareCategoryData = findCategoryById(
        parseInt(compareStore.compareItems[0]?.categoryId),
        categoryStore.categories?.children,
      );
      // check current AND compared product has same categoryID or not on current level - 1? If not, then show confirm modal.
      if (currentItemCategoryData &&
          compareCategoryData &&
          currentItemCategoryData?.parentsNameArr[currentItemCategoryData.parentsNameArr.length - 2]?.categoryId !== compareCategoryData?.parentsNameArr[compareCategoryData.parentsNameArr.length - 2]?.categoryId) {
        modalStore.showCompareCategoryConfirmModal(this.product);
      } else if (this.compareItems.length === this.maxCompareCount) { // if items equal with maxCompareItems
        modalStore.showMaxItemWarningModal();
      } else { // push product to compare store...
        compareStore.pushNewCompareItem(this.product);
        try {
          this.$awAnalytics[PUSH_COMPARE_PRODUCT]({
            productCompareAnalyticsType: PCAT_ADD_PRODUCT,
            productsArr: [this.product],
          });
        } catch (err) {
          this.$logger.error(err);
        }
      }
    },
    videoThumbnailKey (videoId) {
      return useVideoStore().videoCache[videoId]?.thumbnail_url || 0;
    },
    getYoutubeThumbnailSources (id) {
      return useVideoStore().getYoutubeThumbnailSources(id);
    },
    asyncGetYoutubeThumbnailSources (id) {
      return useVideoStore().asyncGetYoutubeThumbnailSources(id);
    },
    priceGross (variantParam) {
      const variant = variantParam || this.getVariant;
      const price = variant?.price?.grossDiscounted;

      return {
        text: this.$price({ gross: price }, variant?.price?.currency),
        value: price,
      };
    },
    priceOriginalGrossByVariant (variantParam) {
      const variant = variantParam || this.getVariant;
      return this.$price({ gross: variant?.price?.gross }, variant?.price?.currency);
    },
    extraWeightPrice (variantParam) {
      const variant = variantParam || this.getVariant;
      return this.$price({ gross: this.product.extraWeightPrice }, variant?.price?.currency);
    },
    getUnitPriceByType (unitPriceType) {
      const variant = this.getVariant;

      const unitPrice = variant?.packageInfo?.unitPrice;
      const pricePerKg = variant?.packageInfo?.pricePerKg;

      const packageUnit = variant?.packageInfo?.packageUnit?.toLowerCase?.();
      const displayPackageUnit = packageUnit === 'liter' ? 'l' : packageUnit;
      return {
        unitPrice: unitPrice ? `${this.$price({ gross: unitPrice[unitPriceType] }, unitPrice.currency)}/${displayPackageUnit}` : '',
        pricePerKg: pricePerKg ? `${this.$price({ gross: pricePerKg[unitPriceType] }, pricePerKg.currency)}/kg` : '',
      };
    },
    priceDiscountedPeriod () {
      const config = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour12: false,
      };
      const a = this.$date(this.price.discountValidFrom, config).replace(/\s/g, '');
      const b = this.$date(this.price.discountValidTo, config).replace(/\s/g, '');

      return {
        from: a,
        to: b,
      };
    },
  },
};
